import React, { useCallback, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import DateRangePicker from '../components/stats/DateRangePicker';
import GranularityToggle from '../components/stats/GranularityToggle';
import StatCards from '../components/stats/StatCards';
import StatChartCard from '../components/stats/StatChartCard';
import { SERIES_COLOR_BLUE, SERIES_COLOR_ORANGE } from '../components/stats/statSeries';
import type { SeriesColor, SeriesKey, StatSeries } from '../components/stats/statSeries';
import useDebouncedDateRange from '../hooks/useDebouncedDateRange';
import useStatisticsOverview from '../hooks/useStatisticsOverview';
import useTimeSeries from '../hooks/useTimeSeries';
import type { StatsGranularity, TimeSeriesPoint } from '../services/types';

/**
 * Baut die Serienliste eines Ein-Serien-Charts (leer, solange die
 * Zeitreihe noch nicht geladen ist). Die Farbe haengt fest an der Serie.
 */
function singleSeries(
  seriesKey: SeriesKey,
  label: string,
  color: SeriesColor,
  points: readonly TimeSeriesPoint[] | undefined
): readonly StatSeries[] {
  if (points === undefined) {
    return [];
  }
  return [{ seriesKey, label, color, points }];
}

/**
 * Statistik-Dashboard (PRIMARY FEATURE): Overview-Kacheln, gemeinsame
 * Filter (Granularitaet + Zeitraum) und vier Zeitreihen-Charts.
 *
 * Alle Charts teilen sich EINEN Filterzustand; leere from/to-Werte
 * bedeuten Server-Defaults (Parameter werden weggelassen).
 *
 * Zeitraum-Verhalten (siehe useDebouncedDateRange):
 * - from/to wirken erst nach 400 ms Debounce; ein Granularitaetswechsel
 *   wirkt dagegen sofort
 * - ein ungueltiger Zeitraum (Von nach Bis) loest KEINE Requests aus: die
 *   Charts behalten den letzten gueltigen Stand und markieren ihn sichtbar
 * - ein Granularitaetswechsel leert einen manuell gewaehlten Zeitraum,
 *   weil die Server-Defaults je Granularitaet sinnvoll gewaehlt sind
 *   (day: 30 Tage, week: 26 Wochen, month: 24 Monate, year: 10 Jahre) und
 *   ein 30-Tage-Fenster in Jahresgranularitaet nur einen Balken ergaebe
 */
function DashboardPage(): React.JSX.Element {
  const [granularity, setGranularity] = useState<StatsGranularity>('day');
  const {
    fromInput,
    toInput,
    applied,
    invalid: rangeInvalid,
    setFrom,
    setTo,
    reset: resetRange,
  } = useDebouncedDateRange();

  const from = applied.from !== '' ? applied.from : undefined;
  const to = applied.to !== '' ? applied.to : undefined;

  const overview = useStatisticsOverview();
  const registrations = useTimeSeries('user-registrations', granularity, from, to);
  const userTotals = useTimeSeries('user-totals', granularity, from, to);
  const propertiesCreated = useTimeSeries('properties-created', granularity, from, to);
  const contractsCreated = useTimeSeries('contracts-created', granularity, from, to);
  const contractsFinalized = useTimeSeries('contracts-finalized', granularity, from, to);

  /**
   * Granularitaetswechsel: neue Stufe setzen UND den Zeitraum sofort
   * leeren. Der Reset wirkt ohne Debounce, damit nur EINE Request-Welle
   * entsteht (nicht erst mit altem, dann mit leerem Zeitraum).
   */
  const handleGranularityChange = useCallback(
    (nextGranularity: StatsGranularity): void => {
      setGranularity(nextGranularity);
      resetRange();
    },
    [resetRange]
  );

  /*
   * Serienlisten memoisieren: Ohne useMemo entstuende bei jedem Render
   * (z. B. Umschalten Diagramm/Tabelle in einer anderen Card) eine neue
   * Array-Referenz, wodurch die Zusammenfuehrung der Datenzeilen in Chart
   * und Tabelle jedes Mal neu liefe.
   */
  const registrationsSeries = useMemo(
    () =>
      singleSeries('value', 'Neue Registrierungen', SERIES_COLOR_BLUE, registrations.data?.points),
    [registrations.data]
  );
  const userTotalsSeries = useMemo(
    () =>
      singleSeries(
        'value',
        'Registrierte Benutzer gesamt',
        SERIES_COLOR_BLUE,
        userTotals.data?.points
      ),
    [userTotals.data]
  );
  const propertiesSeries = useMemo(
    () =>
      singleSeries('value', 'Neue Mieteinheiten', SERIES_COLOR_BLUE, propertiesCreated.data?.points),
    [propertiesCreated.data]
  );

  /** Vertrags-Chart: zwei Serien, erst wenn BEIDE Zeitreihen geladen sind. */
  const contractsSeries: readonly StatSeries[] = useMemo(
    () =>
      contractsCreated.data !== null && contractsFinalized.data !== null
        ? [
            {
              seriesKey: 'created',
              label: 'Angelegt',
              color: SERIES_COLOR_BLUE,
              points: contractsCreated.data.points,
            },
            {
              seriesKey: 'finalized',
              label: 'Finalisiert',
              color: SERIES_COLOR_ORANGE,
              points: contractsFinalized.data.points,
            },
          ]
        : [],
    [contractsCreated.data, contractsFinalized.data]
  );

  return (
    <div id="dashboard-page">
      <h1 id="dashboard-heading" className="page-heading h3 mb-3">
        Dashboard
      </h1>

      {overview.error !== null && (
        <Alert id="overview-error" variant="danger" className="mb-3">
          {overview.error}
        </Alert>
      )}
      <StatCards overview={overview.data} loading={overview.loading} error={overview.error} />

      <div id="dashboard-filters" className="d-flex flex-wrap align-items-end gap-3 mb-4">
        <GranularityToggle value={granularity} onChange={handleGranularityChange} />
        <DateRangePicker
          from={fromInput}
          to={toInput}
          onFromChange={setFrom}
          onToChange={setTo}
          onReset={resetRange}
        />
      </div>

      <Row id="dashboard-charts-row" className="g-3">
        {/* lg={6} statt xl={6}: 992-1199px wuerde die Breite sonst nicht nutzen. */}
        <Col id="chart-registrations-col" xs={12} lg={6}>
          <StatChartCard
            chartId="chart-registrations"
            title="Neue Registrierungen"
            variant="bar"
            granularity={granularity}
            series={registrationsSeries}
            loading={registrations.loading}
            error={registrations.error}
            staleRange={rangeInvalid}
          />
        </Col>
        <Col id="chart-user-totals-col" xs={12} lg={6}>
          <StatChartCard
            chartId="chart-user-totals"
            title="Registrierte Benutzer gesamt"
            variant="line"
            granularity={granularity}
            series={userTotalsSeries}
            loading={userTotals.loading}
            error={userTotals.error}
            staleRange={rangeInvalid}
          />
        </Col>
        <Col id="chart-properties-col" xs={12} lg={6}>
          <StatChartCard
            chartId="chart-properties"
            title="Neue Mieteinheiten"
            variant="bar"
            granularity={granularity}
            series={propertiesSeries}
            loading={propertiesCreated.loading}
            error={propertiesCreated.error}
            staleRange={rangeInvalid}
          />
        </Col>
        <Col id="chart-contracts-col" xs={12} lg={6}>
          <StatChartCard
            chartId="chart-contracts"
            title="Mietverträge"
            variant="bar"
            granularity={granularity}
            series={contractsSeries}
            loading={contractsCreated.loading || contractsFinalized.loading}
            error={contractsCreated.error ?? contractsFinalized.error}
            staleRange={rangeInvalid}
          />
        </Col>
      </Row>
    </div>
  );
}

export default DashboardPage;
